import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import { DEFAULT_LANGUAGE, type LanguageCode } from './languages';
import { detectLanguage } from './detectLanguage';
import { sharedDefaultBundle, loadSharedBundle } from './shared';

// A translation subtree: string leaves nested arbitrarily deep (so 'section.label'
// style keys work), matching what i18next accepts as a resource value.
export interface I18nResourceTree {
  [key: string]: string | I18nResourceTree;
}

// One language's resources: namespace -> nested key/value tree. This is the shape of a
// single per-language locale file (e.g. en.json with `common` and `profile` at the top).
export type LanguageBundle = Record<string, I18nResourceTree>;

// The non-English bundles an app ships, keyed by language code. Each value lazily imports
// that language's locale file. The KEYS are the source of truth for which languages the
// app supports — `Partial` is deliberate: an app lists only what it actually ships, and
// adding a language to the global registry (languages.ts) never forces every app to add a
// loader for it. English is always available via `defaultBundle`, so it is excluded here.
export type LocaleLoaders = Partial<
  Record<Exclude<LanguageCode, 'en'>, () => Promise<{ default: LanguageBundle }>>
>;

// Controller returned by createI18n: the i18next instance plus the helpers the provider
// needs to apply a language lazily.
export interface AppI18n {
  instance: I18nInstance;
  // The language the app wants on startup (detected/stored); applied by the provider
  // once mounted, since the instance itself boots on the eagerly-bundled default.
  initialLanguage: LanguageCode;
  // The languages THIS app ships ('en' plus the loader keys). Drives the language picker,
  // detection, and i18next's supportedLngs so an app never offers (or tries to load) a
  // language it has no bundle for.
  availableLanguages: LanguageCode[];
  // Registers a language's bundle (loading it on first use) so it can be shown.
  ensureLanguage: (code: LanguageCode) => Promise<void>;
}

interface CreateI18nParams {
  // The raw persisted preference (e.g. from localStorage). Resolved against this app's
  // availableLanguages by the detector, so a stored language the app no longer ships
  // falls back cleanly instead of breaking.
  stored?: string | null;
  // The default language's bundle, available synchronously so first paint is never
  // blocked and there is always a fallback. Conventionally English.
  defaultBundle: LanguageBundle;
  // The app's non-English bundles. Its keys define the app's supported language set.
  loaders: LocaleLoaders;
}

// Builds a configured i18next instance that boots with only the default language and
// registers other languages on demand. Uses a private instance (createInstance) rather
// than the global singleton so multiple apps can coexist without clobbering each other.
export function createI18n({ stored, defaultBundle, loaders }: CreateI18nParams): AppI18n {
  // The app's supported set is derived from the loader keys (plus the always-present
  // default). This is the single source of truth for what this app ships; everything
  // downstream (picker, detection, supportedLngs) flows from it, so adding/removing a
  // language is a one-line change to `loaders` in the app's own i18n bootstrap.
  const availableLanguages: LanguageCode[] = [
    DEFAULT_LANGUAGE,
    ...(Object.keys(loaders) as Exclude<LanguageCode, 'en'>[]),
  ];
  const initialLanguage = detectLanguage(stored, availableLanguages);

  const instance = i18n.createInstance();
  // Merge the library-owned `shared` namespace (used by shared components like ReusableTable)
  // with the app's own namespaces. The app keeps full control of its bundles; it just never
  // has to redeclare the shared strings, and shared components resolve against this instance.
  const mergedDefault = { ...sharedDefaultBundle, ...defaultBundle };
  const namespaces = Object.keys(mergedDefault);

  void instance.use(initReactI18next).init({
    // Boot on the eager default; the provider switches to initialLanguage after loading
    // its bundle. This keeps init synchronous and avoids a missing-resource flash of keys.
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    // Only this app's languages; i18next then refuses to switch to anything we cannot load.
    supportedLngs: availableLanguages,
    resources: { [DEFAULT_LANGUAGE]: mergedDefault },
    ns: namespaces,
    defaultNS: 'common',
    fallbackNS: 'common',
    interpolation: { escapeValue: false },
    returnNull: false,
  });

  const ensureLanguage = async (code: LanguageCode) => {
    // The default language is always present; a language this app does not ship is ignored
    // (the picker/detector never produce one, but guard anyway). Others are fetched and
    // registered once. Both the app's bundle and the shared bundle for the language are
    // loaded together so shared component strings follow the selected language. The shared
    // bundle falls back to English if the shared package has no translation for the code.
    if (
      code === DEFAULT_LANGUAGE ||
      !availableLanguages.includes(code) ||
      instance.hasResourceBundle(code, namespaces[0])
    ) {
      return;
    }
    const loader = loaders[code as Exclude<LanguageCode, 'en'>];
    if (!loader) return;
    const [appBundle, sharedBundle] = await Promise.all([loader().then((m) => m.default), loadSharedBundle(code)]);
    for (const [ns, tree] of Object.entries({ ...sharedBundle, ...appBundle })) {
      instance.addResourceBundle(code, ns, tree, true, true);
    }
  };

  return { instance, initialLanguage, availableLanguages, ensureLanguage };
}
